import axios from 'axios';
import { basicAuthConfig } from '../configs';
import { urlMyspreadApi } from '../urls';

/**
 * Obtener tarifarios paginados y filtrados
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - { tipo_tarifario_id, comuna_id, servicio_id, cliente_id, region_id, pageSize, pageNumber }
 * @returns {Promise} Response con lista de tarifarios y total
 */
export const getTarifarios = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/tarifarios/?';
    const query = [];
    
    // Parámetros de paginación obligatorios
    query.push(`pageSize=${params.pageSize || 20}`);
    query.push(`pageNumber=${params.pageNumber || 1}`);
    
    // Parámetros opcionales de filtrado
    if (params.tipo_tarifario_id) query.push(`tipo_tarifario_id=${params.tipo_tarifario_id}`);
    if (params.comuna_id) query.push(`comuna_id=${params.comuna_id}`);
    if (params.servicio_id) query.push(`servicio_id=${params.servicio_id}`);
    if (params.cliente_id) query.push(`cliente_id=${params.cliente_id}`);
    if (params.region_id) query.push(`region_id=${params.region_id}`);
    if (params.tms_id) query.push(`tms_id=${params.tms_id}`);
    
    endpoint += query.join('&');
    const response = await axios.get(endpoint, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data,
        total: response.data.total,
        message: response.data.message
      };
    }
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      total: 0
    };
  } catch (error) {
    console.error('Error al obtener tarifarios:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener tarifarios',
      data: [],
      total: 0
    };
  }
};

/**
 * Obtener tipos de tarifario
 * @param {Object} user - Usuario autenticado
 * @returns {Promise} Response con lista de tipos de tarifario
 */
export const getTiposTarifario = async (user) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/tarifarios/tipos`;
    
    const response = await axios.get(endpoint, config);
    
    return {
      success: true,
      data: response.data.data || [],
      total: response.data.total || 0,
      message: response.data.message || 'Tipos de tarifario obtenidos exitosamente'
    };
  } catch (error) {
    console.error('Error al obtener tipos de tarifario:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener tipos de tarifario',
      data: [],
      total: 0
    };
  }
};

/**
 * Obtener resumen de errores de tarifarios
 * @param {Object} user - Usuario autenticado
 * @returns {Promise} Response con resumen de errores
 */
export const getResumenErroresTarifarios = async (user) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/tarifarios/errores/resumen`;
    
    const response = await axios.get(endpoint, config);
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Resumen de errores obtenido exitosamente'
    };
  } catch (error) {
    console.error('Error al obtener resumen de errores:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener resumen de errores',
      data: {}
    };
  }
};

/**
 * Obtener detalle de errores de tarifarios por tipo
 * @param {Object} user - Usuario autenticado
 * @param {number} tipoTarifarioId - ID del tipo de tarifario
 * @param {Object} params - { region_id }
 * @returns {Promise} Response con detalle de errores
 */
export const getDetalleErroresTarifarios = async (user, tipoTarifarioId, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/tarifarios/errores/detalle?tipo_tarifario_id=${tipoTarifarioId}`;
    
    if (params.region_id) {
      endpoint += `&region_id=${params.region_id}`;
    }
    
    const response = await axios.get(endpoint, config);
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Detalle de errores obtenido exitosamente'
    };
  } catch (error) {
    console.error('Error al obtener detalle de errores:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener detalle de errores',
      data: {}
    };
  }
};

/**
 * Crear tarifarios masivamente
 * @param {Object} user - Usuario autenticado
 * @param {Array} tarifarios - Array de objetos tarifario para crear
 * @returns {Promise} Response con resultado de la operación
 */
export const createTarifariosMasivo = async (user, tarifarios) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/tarifarios/`;
    
    const response = await axios.post(endpoint, tarifarios, config);
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Tarifarios creados exitosamente'
    };
  } catch (error) {
    console.error('Error al crear tarifarios:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al crear tarifarios'
    };
  }
};

/**
 * Actualizar tarifarios (individual o bulk)
 * @param {Object} user - Usuario autenticado
 * @param {Array} tarifarios - Array de objetos con { id, precio, modalidad }
 * @returns {Promise} Response con resultado de la operación
 */
export const updateTarifarios = async (user, tarifarios) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/tarifarios`;
    
    const body = {
      data: tarifarios
    };
    
    const response = await axios.put(endpoint, body, config);
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Tarifarios actualizados exitosamente'
    };
  } catch (error) {
    console.error('Error al actualizar tarifarios:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al actualizar tarifarios',
      details: error.response?.data?.details || null
    };
  }
};

/**
 * Obtener detalle de errores de empresa
 * @param {Object} user - Usuario autenticado
 * @param {Object} params - { region_id }
 * @returns {Promise} Response con detalle de errores de empresa
 */
export const getDetalleErroresEmpresa = async (user, params = {}) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/tarifarios/errores/detalle?empresa=true`;
    
    if (params.region_id) {
      endpoint += `&region_id=${params.region_id}`;
    }
    
    const response = await axios.get(endpoint, config);
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Detalle de errores de empresa obtenido exitosamente'
    };
  } catch (error) {
    console.error('Error al obtener detalle de errores de empresa:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener detalle de errores de empresa',
      data: {}
    };
  }
};

/**
 * Obtener precios de empresa paginados y filtrados
 * @param {Object} user - Usuario autenticado
 * @param {string} params - Parámetros de consulta en formato query string
 * @returns {Promise} Response con lista de precios de empresa y paginación
 */
export const getPreciosEmpresa = async (user, params = '') => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/tarifarios/precios-empresa`;
    
    if (params && typeof params === 'string') {
      endpoint += params.startsWith('?') ? params : `?${params}`;
    }
    
    const response = await axios.get(endpoint, config);
    
    if (response.data) {
      return {
        success: true,
        data: response.data.data || [],
        pagination: response.data.pagination || {},
        message: response.data.message
      };
    }
    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: [],
      pagination: {}
    };
  } catch (error) {
    console.error('Error al obtener precios de empresa:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al obtener precios de empresa',
      data: [],
      pagination: {}
    };
  }
};

/**
 * Actualizar precios de empresa masivamente
 * @param {Object} user - Usuario autenticado
 * @param {Array} preciosActualizados - Array de objetos con id y nuevos valores
 * @returns {Promise} Response con resultado de la operación
 */
export const updatePreciosEmpresaMasivo = async (user, preciosActualizados) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/tarifarios/precios-empresa`;
    
    const response = await axios.put(endpoint, { data: preciosActualizados }, config);
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Precios de empresa actualizados exitosamente'
    };
  } catch (error) {
    console.error('Error al actualizar precios de empresa:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al actualizar precios de empresa'
    };
  }
};

/**
 * Crear precios de empresa masivamente
 * @param {Object} user - Usuario autenticado
 * @param {Array} preciosEmpresa - Array de objetos precio de empresa para crear
 * @returns {Promise} Response con resultado de la operación
 */
export const createPreciosEmpresaMasivo = async (user, preciosEmpresa) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/tarifarios/precios-empresa`;
    
    const response = await axios.post(endpoint, preciosEmpresa, config);
    
    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Precios de empresa creados exitosamente'
    };
  } catch (error) {
    console.error('Error al crear precios de empresa:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Error al crear precios de empresa'
    };
  }
};
